import { UserMediaDevicePlatformError } from '@openphone/desktop-client'

import { mapDOMExceptionToUMDAPlatformErrorSubType } from '@src/app/voice/user-media-device-store/mapDOMExceptionToUMDAPlatformErrorSubType'

export default abstract class UMDAPlatformStrategy {
  private static readonly defaultAudioConstraints: MediaTrackConstraints = {
    autoGainControl: { ideal: true },
    echoCancellation: { ideal: true },
    noiseSuppression: { ideal: true },
    // @ts-expect-error voiceIsolation is not a web-standard property, but is supported on some browsers.
    voiceIsolation: { ideal: true },
  }

  constructor(readonly platform: string) {}

  abstract hasMicrophoneAccess(): Promise<true | UserMediaDevicePlatformError>

  /**
   * Requests microphone access with the default constraints, following best practices recommended by Twilio.
   *
   * {@link https://www.twilio.com/docs/voice/sdks/javascript/best-practices#working-with-microphones-and-getusermedia View Twilio Documentation}
   */
  public async requestMicrophoneAccess(
    additionalConstraints: MediaTrackConstraints = {},
  ) {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...UMDAPlatformStrategy.defaultAudioConstraints,
          ...additionalConstraints,
        },
      })

      // Calling getUserMedia will start the media track selected.
      // This is not desired as the user may get the impression the mic is in use.
      // Therefore, we want to avoid having tracks started when they're not needed.
      // We only wanted to get the input device list so we stop the tracks immediately
      mediaStream.getTracks().forEach((track) => track.stop())
    } catch (error) {
      const isDOMException = error instanceof DOMException
      return new UserMediaDevicePlatformError(
        {
          mediaDevice: 'microphone',
          platform: this.platform,
          originalError:
            error instanceof Error
              ? error
              : isDOMException
                ? error
                : new Error(`${error}`),
        },
        isDOMException ? mapDOMExceptionToUMDAPlatformErrorSubType(error) : 'unknown',
      )
    }
  }
}
